import type { ConfigType } from '@plone/registry';

import CardEdit from '../components/Blocks/Card/Edit';
import CardView from '../components/Blocks/Card/View';
import { CardSchema } from '../components/Blocks/Card/schema';
import ColumnGridEdit from '../components/Blocks/ColumnGrid/Edit';
import ColumnGridView from '../components/Blocks/ColumnGrid/View';
import EspecialitatsListingTemplate from '../components/Blocks/Listing/EspecialitatsListingTemplate';
import { especialitatsListingSchemaEnhancer } from '../components/Blocks/Listing/especialitatsListingSchema';
import UnitatsListingTemplate from '../components/Blocks/Listing/UnitatsListingTemplate';
import { unitatsListingSchemaEnhancer } from '../components/Blocks/Listing/unitatsListingSchema';
import BlogListingTemplate from '../components/Blocks/Listing/BlogListingTemplate';
import { blogListingSchemaEnhancer } from '../components/Blocks/Listing/blogListingSchema';
import NewsListingTemplate from '../components/Blocks/Listing/NewsListingTemplate';
import { newsListingSchemaEnhancer } from '../components/Blocks/Listing/newsListingSchema';
import FullwidthImageEdit from '../components/Blocks/FullwidthImage/Edit';
import FullwidthImageView from '../components/Blocks/FullwidthImage/View';
import { FullwidthImageSchema } from '../components/Blocks/FullwidthImage/schema';
import GoogleMapEdit from '../components/Blocks/GoogleMap/Edit';
import GoogleMapView from '../components/Blocks/GoogleMap/View';
import { GoogleMapSchema } from '../components/Blocks/GoogleMap/schema';
import HomepageContactEdit from '../components/Blocks/HomepageContact/Edit';
import HomepageContactView from '../components/Blocks/HomepageContact/View';
import { HomepageContactSchema } from '../components/Blocks/HomepageContact/schema';
import HomepageIconsEdit from '../components/Blocks/HomepageIcons/Edit';
import HomepageIconsView from '../components/Blocks/HomepageIcons/View';
import { HomepageIconsSchema } from '../components/Blocks/HomepageIcons/schema';
import HomepageRolloverEdit from '../components/Blocks/HomepageRollover/Edit';
import HomepageRolloverView from '../components/Blocks/HomepageRollover/View';
import { HomepageRolloverSchema } from '../components/Blocks/HomepageRollover/schema';
import HomepageSliderEdit from '../components/Blocks/HomepageSlider/Edit';
import HomepageSliderView from '../components/Blocks/HomepageSlider/View';
import { HomepageSliderSchema } from '../components/Blocks/HomepageSlider/schema';
import HomepageAlertEdit from '../components/Blocks/HomepageAlert/Edit';
import HomepageAlertView from '../components/Blocks/HomepageAlert/View';
import { HomepageAlertSchema } from '../components/Blocks/HomepageAlert/schema';
import IconCardGridEdit from '../components/Blocks/IconCardGrid/Edit';
import IconCardGridView from '../components/Blocks/IconCardGrid/View';
import { IconCardGridSchema } from '../components/Blocks/IconCardGrid/schema';
import SectionHeadingEdit from '../components/Blocks/SectionHeading/Edit';
import SectionHeadingView from '../components/Blocks/SectionHeading/View';
import { SectionHeadingSchema } from '../components/Blocks/SectionHeading/schema';
import SectionTextEdit from '../components/Blocks/SectionText/Edit';
import SectionTextView from '../components/Blocks/SectionText/View';
import { SectionTextSchema } from '../components/Blocks/SectionText/schema';
import TabsEdit from '../components/Blocks/Tabs/Edit';
import TabsView from '../components/Blocks/Tabs/View';
import { TabsSchema } from '../components/Blocks/Tabs/schema';
import TimelineEdit from '../components/Blocks/Timeline/Edit';
import TimelineView from '../components/Blocks/Timeline/View';
import { TimelineSchema } from '../components/Blocks/Timeline/schema';
import TitleView from '../components/Blocks/Title/View';

import imagesSVG from '@plone/volto/icons/images.svg';

import { cardSchemaEnhancer } from '../components/Blocks/Card/schema';
import { applyListingBatchSize } from '../helpers/listingPagination';

import {
  COLUMN_GRID_ALLOWED_BLOCKS,
  ICON_CARD_GRID_ALLOWED_BLOCKS,
  TAB_ALLOWED_BLOCKS,
  pickNestedBlocksConfig,
} from './nestedBlocks';

export {
  COLUMN_GRID_ALLOWED_BLOCKS,
  ICON_CARD_GRID_ALLOWED_BLOCKS,
  TAB_ALLOWED_BLOCKS,
};

export default function installBlocks(config: ConfigType) {
  config.blocks.blocksConfig.title = {
    ...config.blocks.blocksConfig.title,
    view: TitleView,
  };

  config.blocks.blocksConfig.homepageSlider = {
    id: 'homepageSlider',
    title: 'Homepage slider',
    icon: imagesSVG,
    group: 'common',
    view: HomepageSliderView,
    edit: HomepageSliderEdit,
    blockSchema: HomepageSliderSchema,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
  };

  config.blocks.blocksConfig.homepageIcons = {
    id: 'homepageIcons',
    title: 'Homepage icons',
    icon: imagesSVG,
    group: 'common',
    view: HomepageIconsView,
    edit: HomepageIconsEdit,
    blockSchema: HomepageIconsSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.homepageAlert = {
    id: 'homepageAlert',
    title: 'Homepage alert',
    icon: imagesSVG,
    group: 'common',
    view: HomepageAlertView,
    edit: HomepageAlertEdit,
    blockSchema: HomepageAlertSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.homepageRollover = {
    id: 'homepageRollover',
    title: 'Homepage rollover',
    icon: imagesSVG,
    group: 'common',
    view: HomepageRolloverView,
    edit: HomepageRolloverEdit,
    blockSchema: HomepageRolloverSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.homepageContact = {
    id: 'homepageContact',
    title: 'Homepage contact',
    icon: imagesSVG,
    group: 'common',
    view: HomepageContactView,
    edit: HomepageContactEdit,
    blockSchema: HomepageContactSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.csjSectionHeading = {
    id: 'csjSectionHeading',
    title: 'Capçalera de secció',
    icon: imagesSVG,
    group: 'common',
    view: SectionHeadingView,
    edit: SectionHeadingEdit,
    blockSchema: SectionHeadingSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.csjCard = {
    id: 'csjCard',
    title: 'Targeta CSJ',
    icon: imagesSVG,
    group: 'common',
    view: CardView,
    edit: CardEdit,
    blockSchema: CardSchema,
    sidebarTab: 1,
    mostUsed: true,
    variations: [
      { id: 'photo', title: 'Imatge (30/70)', isDefault: true },
      { id: 'icon', title: 'Icona horitzontal' },
      { id: 'stacked', title: 'Icona centrada' },
      { id: 'label-top', title: 'Icona centrada sota' },
    ],
    schemaEnhancer: cardSchemaEnhancer,
  };

  config.blocks.blocksConfig.csjSectionText = {
    id: 'csjSectionText',
    title: 'Text de secció',
    icon: imagesSVG,
    group: 'common',
    view: SectionTextView,
    edit: SectionTextEdit,
    blockSchema: SectionTextSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.csjIconCardGrid = {
    id: 'csjIconCardGrid',
    title: 'Graella icones',
    icon: imagesSVG,
    group: 'common',
    view: IconCardGridView,
    edit: IconCardGridEdit,
    blockSchema: IconCardGridSchema,
    sidebarTab: 1,
    allowedBlocks: [...ICON_CARD_GRID_ALLOWED_BLOCKS],
    blocksConfig: pickNestedBlocksConfig(
      config.blocks.blocksConfig,
      ICON_CARD_GRID_ALLOWED_BLOCKS,
    ),
    blockHasOwnFocusManagement: true,
  };

  config.blocks.blocksConfig.csjFullwidthImage = {
    id: 'csjFullwidthImage',
    title: 'Imatge amplada completa',
    icon: imagesSVG,
    group: 'common',
    view: FullwidthImageView,
    edit: FullwidthImageEdit,
    blockSchema: FullwidthImageSchema,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.csjGoogleMap = {
    id: 'csjGoogleMap',
    title: 'Mapa Google',
    icon: imagesSVG,
    group: 'common',
    view: GoogleMapView,
    edit: GoogleMapEdit,
    blockSchema: GoogleMapSchema,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
  };

  config.blocks.blocksConfig.csjColumnGrid = {
    id: 'csjColumnGrid',
    title: 'Graella de columnes',
    icon: imagesSVG,
    group: 'common',
    view: ColumnGridView,
    edit: ColumnGridEdit,
    sidebarTab: 1,
    allowedBlocks: [...COLUMN_GRID_ALLOWED_BLOCKS],
    blocksConfig: pickNestedBlocksConfig(
      config.blocks.blocksConfig,
      COLUMN_GRID_ALLOWED_BLOCKS,
    ),
    blockHasOwnFocusManagement: true,
  };

  config.blocks.blocksConfig.csjTabs = {
    id: 'csjTabs',
    title: 'Pestanyes CSJ',
    icon: imagesSVG,
    group: 'common',
    view: TabsView,
    edit: TabsEdit,
    blockSchema: TabsSchema,
    sidebarTab: 1,
    allowedBlocks: [...TAB_ALLOWED_BLOCKS],
    blocksConfig: pickNestedBlocksConfig(
      config.blocks.blocksConfig,
      TAB_ALLOWED_BLOCKS,
    ),
    blockHasOwnFocusManagement: true,
  };

  config.blocks.blocksConfig.csjTimeline = {
    id: 'csjTimeline',
    title: 'Timeline CSJ',
    icon: imagesSVG,
    group: 'common',
    view: TimelineView,
    edit: TimelineEdit,
    blockSchema: TimelineSchema,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
  };

  const listingConfig = config.blocks.blocksConfig.listing;
  const listingVariations = listingConfig?.variations || [];
  const originalListingGetAsyncData = listingConfig?.getAsyncData;

  const upsertListingVariation = (variations, variation) => {
    const exists = variations.some((entry) => entry.id === variation.id);
    return exists
      ? variations.map((entry) =>
          entry.id === variation.id ? { ...entry, ...variation } : entry,
        )
      : [...variations, variation];
  };

  const csjListingVariations = [
    {
      id: 'especialitats',
      title: "Llistat d'especialitats",
      template: EspecialitatsListingTemplate,
      schemaEnhancer: especialitatsListingSchemaEnhancer,
    },
    {
      id: 'unitats',
      title: "Llistat d'unitats especialitzades",
      template: UnitatsListingTemplate,
      schemaEnhancer: unitatsListingSchemaEnhancer,
      fullobjects: true,
    },
    {
      id: 'blog',
      title: 'Llistat del blog',
      template: BlogListingTemplate,
      schemaEnhancer: blogListingSchemaEnhancer,
      fullobjects: true,
    },
    {
      id: 'noticies',
      title: 'Llistat de notícies',
      template: NewsListingTemplate,
      schemaEnhancer: newsListingSchemaEnhancer,
      fullobjects: true,
      customPagination: true,
    },
  ];

  const variationsWithCsjTemplates = csjListingVariations.reduce(
    upsertListingVariation,
    listingVariations,
  );

  config.blocks.blocksConfig.listing = {
    ...listingConfig,
    variations: variationsWithCsjTemplates,
    ...(originalListingGetAsyncData
      ? {
          getAsyncData: (props) =>
            originalListingGetAsyncData({
              ...props,
              data: applyListingBatchSize(props.data),
            }),
        }
      : {}),
  };

  return config;
}
